import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
  Box,
  MenuItem,
  Select as MuiSelect,
  SelectChangeEvent,
  SxProps,
  Theme,
  Typography
} from '@mui/material';
import React, { ReactNode } from 'react';
import { InputProps } from './input';
import { Label } from './label';

/**
 * Primary UI component for user interaction
 */

export interface SelectProps<T = any> {
  /**
   * Placeholder of the input
   * */
  readonly placeholder?: string;
  /**
   * Label of the input
   * */
  readonly label?: string;
  /**
   * Disabled state of the input
   * */
  readonly disabled?: boolean;
  /**
   * Name of the input
   * */
  readonly name?: string;
  /**
   * Value of the input
   *  */
  readonly value?: T | undefined;
  /**
   * On change handler of the input
   * */
  readonly onChange?: (event: SelectChangeEvent<string>) => void;
  /**
   * Is input required ?
   * */
  readonly required?: boolean;
  /**
   * Children of the input
   * */
  readonly children?: ReactNode;
  /**
   *
   * @param value Value of the select
   * @returns
   */
  readonly renderValue?: (value: T) => ReactNode;
  /**
   * Helper text on bottom
   */
  readonly helperText?: string;
  /**
   * Sx props of the input
   * */
  readonly sx?: SxProps<Theme>;
  /**
   * Display empty value
   */
  readonly displayEmpty?: boolean;
  /*
   * Error state of the input
   */
  readonly error?: boolean;
}

export function Select({
  placeholder,
  label,
  sx,
  children,
  helperText,
  displayEmpty,
  error = false,
  ...props
}: SelectProps) {
  return (
    <Box sx={{ width: '100%', ...sx }}>
      <Label text={label} error={error} />
      <MuiSelect
        IconComponent={ExpandMoreRoundedIcon}
        {...props}
        sx={{
          '&.MuiInputBase-root': {
            cursor: 'pointer',
            backgroundColor: theme => theme.brandPalette.background.secondary,

            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center'
            },

            '& .MuiSelect-icon': {
              width: theme => theme.spacing(9),
              color: theme => theme.brandPalette.foreground.inputIcon
            },
            '& .Mui-disabled': {
              color: theme => theme.brandPalette.foreground.disabled
            },
            '&:hover': {
              backgroundColor: theme => theme.brandPalette.border.neutralInverse,
              borderColor: theme => theme.brandPalette.border.neutral
            }
          }
        }}
        displayEmpty={displayEmpty}
        defaultValue={placeholder}
        MenuProps={{
          PaperProps: {
            sx: {
              marginTop: theme => theme.spacing(3),
              padding: theme => theme.spacing(3),
              borderRadius: theme => theme.radius.xl,
              boxShadow: theme => theme.effects.shadows.l,

              '& .MuiList-root': {
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              },
              '& .MuiMenuItem-root': {
                borderRadius: theme => theme.radius.md
              }
            }
          }
        }}
      >
        {placeholder && (
          <MenuItem disabled value={placeholder} selected>
            <Typography
              sx={{
                WebkitTextFillColor: theme => theme.brandPalette.foreground.disabled,
                opacity: 1
              }}
              variant="labelSmall"
            >
              {placeholder}
            </Typography>
          </MenuItem>
        )}
        {children}
      </MuiSelect>
      <HelpText helperText={helperText} error={error} />
    </Box>
  );
}

interface HelpTextProps extends Pick<InputProps, 'helperText'> {
  error: boolean;
}

function HelpText(props: HelpTextProps) {
  if (props.helperText == null) return;

  return (
    <Typography
      variant="labelXSmall"
      sx={{
        color: theme =>
          props.error ? theme.brandPalette.foreground.negativeOffset : theme.palette.text.secondary
      }}
    >
      {props.helperText}
    </Typography>
  );
}
